import {
  Badge,
  ButtonList,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  Table,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { ScheduleListItem } from 'lib-shared-types-api';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { BUSINESS_HOURS_MAX_NUMBER_SCHEDULES } from 'app/app.constants';

import { useOpeningHoursContext } from '../../openingHours.context';
import { ScheduleModalType } from '../../openingHours.view';

import { TableFlows } from './components/tableFlows/tableFlows';
import styles from './tableRow.module.scss';

type Props = {
  onModalOpen: (
    modalType: null | ScheduleModalType,
    schedule: null | ScheduleListItem,
  ) => () => void;
  schedule: ScheduleListItem;
};

export const TableRow = ({ schedule, onModalOpen }: Props): JSX.Element => {
  const {
    id,
    name,
    number_of_conflicts: numberOfConflicts,
    number_of_flows: numberOfFlows,
    timezone,
  } = schedule;

  const { totalNumberOfSchedules } = useOpeningHoursContext();
  const navigate = useNavigate();

  const handleEditClick = useCallback(() => {
    ampli.businessHoursOptionsMenuOptionEditClicked();
    navigate(`/settings/businesshours/${id}`);
  }, [id, navigate]);

  const handleModalOpen = useCallback(
    (modalType: null | ScheduleModalType, toggleDropdown: VoidFunction) =>
      () => {
        switch (modalType) {
          case 'rename':
            ampli.businessHoursOptionsMenuOptionRenameClicked();
            break;
          case 'duplicate':
            ampli.businessHoursOptionsMenuOptionDuplicateClicked();
            break;
          case 'delete':
            ampli.businessHoursOptionsMenuOptionDeleteClicked();
            break;
        }

        toggleDropdown();
        onModalOpen(modalType, schedule)();
      },
    [schedule, onModalOpen],
  );

  const hasReachedMaxNumberSchedules =
    totalNumberOfSchedules >= BUSINESS_HOURS_MAX_NUMBER_SCHEDULES;

  return (
    <Table.Tr
      key={id}
      rowId={id}
      expandableContent={
        <TableFlows numberOfFlows={numberOfFlows} scheduleId={id} />
      }
    >
      <Table.Td className={styles.tableName}>
        <FlexBox alignItems="center">
          <Text.Paragraph variant="2">{name}</Text.Paragraph>
          {numberOfConflicts > 0 && (
            <Badge className={styles.badge} size="small" variant="warning">
              {numberOfConflicts}{' '}
              {numberOfConflicts === 1 ? 'conflict' : 'conflicts'}
            </Badge>
          )}
        </FlexBox>
      </Table.Td>
      <Table.Td>{timezone}</Table.Td>
      <Table.Td className={styles.tableFlow}>{`${numberOfFlows} ${
        numberOfFlows === 1 ? 'flow' : 'flows'
      }`}</Table.Td>
      <Table.Td>
        <DropdownButton
          icon="dots-h"
          fixed
          buttonProps={{ variant: 'tertiary' }}
          noArrow
          onToggle={(isOpen) => {
            if (isOpen) {
              ampli.businessHoursOptionsMenuOpened();
            }
          }}
          dropdown={
            <DropdownButtonContext.Consumer>
              {({ toggleDropdown }) => (
                <ButtonList>
                  <ButtonList.Item onClick={handleEditClick}>
                    Edit schedule
                  </ButtonList.Item>
                  <ButtonList.Item
                    onClick={handleModalOpen('rename', toggleDropdown)}
                  >
                    Rename
                  </ButtonList.Item>
                  <Tooltip
                    content="You need to delete a schedule before you can add a new one"
                    disabled={!hasReachedMaxNumberSchedules}
                    side="left"
                    wrap="none"
                  >
                    <ButtonList.Item
                      isDisabled={hasReachedMaxNumberSchedules}
                      onClick={handleModalOpen('duplicate', toggleDropdown)}
                    >
                      Duplicate
                    </ButtonList.Item>
                  </Tooltip>
                  <ButtonList.Divider />
                  <ButtonList.Item
                    isDisabled={!!numberOfFlows}
                    onClick={handleModalOpen('delete', toggleDropdown)}
                  >
                    Delete schedule
                  </ButtonList.Item>
                </ButtonList>
              )}
            </DropdownButtonContext.Consumer>
          }
        />
      </Table.Td>
    </Table.Tr>
  );
};
